function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not prepare the file."));
        return;
      }
      const separator = result.indexOf(",");
      if (separator < 0) {
        reject(new Error("Could not prepare the file."));
        return;
      }
      resolve(result.slice(separator + 1));
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read the file."));
    reader.readAsDataURL(blob);
  });
}

function safeFileName(fileName: string): string {
  const cleaned = fileName.replace(/[<>:"/\\|?*\u0000-\u001F]/g, "_").trim();
  return cleaned || "download";
}

/** Save fetched bytes through Capacitor. A selected-record guard runs before persistence and later share UI;
 * it cannot cancel a filesystem write already issued to the native bridge. Existing callers need no guard. */
export async function saveNativeDownload(blob: Blob, fileName: string, assertCurrent?: () => void): Promise<void> {
  assertCurrent?.();
  const [{ Directory, Filesystem }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const permissions = await Filesystem.checkPermissions();
  if (permissions.publicStorage !== "granted") {
    const requested = await Filesystem.requestPermissions();
    if (requested.publicStorage !== "granted") {
      throw new Error("Storage permission is required to save the file.");
    }
  }

  const name = safeFileName(fileName);
  const path = `KontrataPH/${name}`;
  const data = await blobToBase64(blob);
  assertCurrent?.();
  const { uri } = await Filesystem.writeFile({
    path,
    data,
    directory: Directory.Documents,
    recursive: true,
  });
  assertCurrent?.();

  // The file is already saved even when the device has no share-capable app.
  const { value: canShare } = await Share.canShare().catch(() => ({ value: false }));
  assertCurrent?.();
  if (canShare) {
    await Share.share({
      title: name,
      text: "File downloaded from KontrataPH.",
      files: [uri],
      dialogTitle: "Save file",
    }).catch(() => {});
  }
}
